import styled from "@emotion/styled";
import { Breakpoints, darkGrey, lightGrey2, PLUS_JAKARTA_SANS } from "@stepfinance/react";
import { Card, Statistic } from "antd";
import type { CardInterface } from "antd/lib/card";
import type { FC } from "react";
import { useEffect, useMemo } from "react";

import { ReactComponent as CollateralisedDebtPositionsIcon } from "../../../assets/collateraliseddebt.svg";
import { useLanguageContext } from "../../../contexts/language/language";
import { useLendingContext } from "../../../contexts/lending";
import { useMarkets } from "../../../contexts/market";
import { useCalculatePriceForDisplayCurrency } from "../../../hooks";
import type { CollapseControlProps } from "../../../hooks/home/useCollapseControl";
import { isIsolatedVaultLendingData, isNFTLendingData, isSolendPlatformLendingData } from "../../../types/lending";
import { getColumns } from "../../../utils/columns";
import { formatRows, getSupplyBorrowValue } from "../../../utils/lending/utils";
import { capitalizeText } from "../../../utils/utils";
import { ChevronCollapse } from "../ChevronCollapse/ChevronCollapse";
import { lendingColumnMap } from "./columns/collateralColumns";
import { nftLendingColumnMap } from "./columns/nftLendingColumns";
import { yieldColumnsMap } from "./columns/yieldColumns";
import { LendingPanel } from "./LendingPanel";

// Commented out but not deleted in case we need to reuse this for the lending logic change.
// const isReformattedLPCollateralizedPosition = (
//   position: ReformattedCollateralizedPosition | ReformattedLPCollateralizedPosition,
// ): position is ReformattedLPCollateralizedPosition => {
//   return (
//     typeof (position as ReformattedLPCollateralizedPosition).asset === "object" &&
//     typeof (position as ReformattedLPCollateralizedPosition).tokenSymbol === "object" &&
//     typeof (position as ReformattedLPCollateralizedPosition).lpDescription !== undefined
//   );
// };

export const Lending: FC<CollapseControlProps> = ({ activePanels, getPanelKey, onPanelClick }) => {
  const { platforms, setLendingBalance } = useLendingContext();
  const { priceInUSD } = useMarkets();
  const calculatePriceForDisplayCurrency = useCalculatePriceForDisplayCurrency();

  const {
    currentLanguageData: {
      common,
      components: { table },
      pages: {
        dashboard: {
          modules: {
            lending,
            nftModule,
            staking: { singleAssetStaking },
          },
        },
        nftGallery,
      },
    },
  } = useLanguageContext();

  const filteredPlatforms = useMemo(() => {
    return Object.entries(platforms).filter(([_, lendingData]) => {
      const hasLendingPositions = Object.keys(lendingData.collateral).length !== 0;
      const displayableRows = Object.entries(lendingData.collateral).filter(([, { display }]) => display).length !== 0;

      const hasLYFPositions = Object.keys(lendingData.yieldFarms).length !== 0;
      const hasIsolatedPoolPositions =
        isSolendPlatformLendingData(lendingData) &&
        Object.values(lendingData.isolatedPools).some((collateralMap) => Object.keys(collateralMap).length > 0);

      const hasIsolatedVaultPositions =
        isIsolatedVaultLendingData(lendingData) &&
        Object.values(lendingData.isolatedVaults).some((vaultData) => Object.keys(vaultData.collateralMap).length > 0);

      const hasNFTLendingPositions = isNFTLendingData(lendingData) && lendingData.nftLending.nftCollateral.length > 0;

      if (
        (!hasLendingPositions || !displayableRows) &&
        !hasLYFPositions &&
        !hasIsolatedPoolPositions &&
        !hasIsolatedVaultPositions &&
        !hasNFTLendingPositions
      ) {
        return false;
      } else {
        return true;
      }
    });
  }, [platforms]);

  const [valueOfSuppliedCollateral, valueOfBorrowedAssets, totalCollateralisedDebtValue] = useMemo(() => {
    let valueOfSupplied = 0;
    let valueOfBorrowed = 0;
    for (const [platform, { collateral, yieldFarms }] of filteredPlatforms) {
      const [suppliedValue, borrowedValue] = getSupplyBorrowValue(platform, collateral, priceInUSD);
      valueOfSupplied += suppliedValue;
      valueOfBorrowed += borrowedValue;

      for (const [, { equityUSD, debtTotalValueUSD }] of Object.entries(yieldFarms)) {
        if (equityUSD && debtTotalValueUSD) {
          valueOfSupplied += equityUSD;
          // Borrowed amount excluded for leveraged yield farms.
          // See: https://github.com/step-finance/step-frontend/pull/624
        }
      }
      if (platform === "solend" || platform === "larix") {
        for (const collateralMap of Object.values(platforms[platform].isolatedPools)) {
          const [isolatedPoolSuppliedValue, isolatedPoolBorrowedValue] = getSupplyBorrowValue(
            platform,
            collateralMap,
            priceInUSD,
          );
          valueOfSupplied += isolatedPoolSuppliedValue;
          valueOfBorrowed += isolatedPoolBorrowedValue;
        }
      }
      if (platform === "ratio" || platform === "hedge") {
        for (const { collateralMap } of Object.values(platforms[platform].isolatedVaults)) {
          const [isolatedVaultSuppliedValue, isolatedVaultBorrowedValue] = getSupplyBorrowValue(
            platform,
            collateralMap,
            priceInUSD,
          );
          valueOfSupplied += isolatedVaultSuppliedValue;
          valueOfBorrowed += isolatedVaultBorrowedValue;
        }
      }
      if (platform === "sharky") {
        const totalValueUSD = platforms[platform].nftLending.totalValueUSD;
        valueOfSupplied += totalValueUSD.supplied ?? 0;
        valueOfBorrowed += totalValueUSD.borrowed ?? 0;
      }
    }
    const totalCollateralisedDebtValue = valueOfSupplied - valueOfBorrowed;

    return [valueOfSupplied, valueOfBorrowed, totalCollateralisedDebtValue];
  }, [filteredPlatforms, platforms, priceInUSD]);

  useEffect(() => {
    setLendingBalance(totalCollateralisedDebtValue);
  }, [setLendingBalance, totalCollateralisedDebtValue]);

  return filteredPlatforms.length > 0 ? (
    <LendingContainer>
      <ChevronCollapse
        title={lending.title}
        value={totalCollateralisedDebtValue}
        TitleIcon={CollateralisedDebtPositionsIcon}
        activePanels={activePanels}
        panelKey={getPanelKey("Lending")}
        onChange={onPanelClick}
      >
        <>
          <CardContainer>
            <StyledCard bordered={false}>
              <StyledStatistic
                title={lending.supplied}
                value={calculatePriceForDisplayCurrency(Number(valueOfSuppliedCollateral))}
              />
            </StyledCard>
            <StyledCard bordered={false}>
              <StyledStatistic
                title={lending.borrowed}
                value={calculatePriceForDisplayCurrency(Number(valueOfBorrowedAssets))}
              />
            </StyledCard>
          </CardContainer>
          {filteredPlatforms.map(([platform, lendingData], index) => {
            const platformName = capitalizeText(platform);
            const {
              rows,
              yieldRows,
              isolatedPools,
              isolatedVaults,
              platformTotalNetLeveragedYieldValue,
              platformTotalNetCollateralValue,
              totalCollateralAPY,
            } = formatRows(platformName, lendingData, priceInUSD);

            return (
              <div key={`${platformName}-${index}`}>
                {rows.length > 0 && (
                  <LendingPanel
                    localStorageKey={`${platform}RowsCollapse`}
                    platform={platformName}
                    totalValue={platformTotalNetCollateralValue}
                    columns={getColumns({
                      selector: platformName,
                      map: lendingColumnMap,
                      args: {
                        totalAPY: totalCollateralAPY ?? 0,
                        commonText: common,
                        lendingText: lending,
                        tableText: table,
                      },
                    })}
                    dataSource={rows}
                  />
                )}
                {(platform === "solend" || platform === "larix") &&
                  Object.entries(isolatedPools).map(([poolName, { rows, totalNetValue, poolYieldAPY }]) => (
                    <LendingPanel
                      key={`${platform}-${poolName}-pool`}
                      localStorageKey={`${platform}${poolName}Collapse`}
                      platform={platformName}
                      title={`${poolName} ${common.pool}`}
                      totalValue={totalNetValue}
                      columns={getColumns({
                        selector: platformName,
                        map: lendingColumnMap,
                        args: {
                          totalAPY: poolYieldAPY ?? 0,
                          commonText: common,
                          lendingText: lending,
                          tableText: table,
                        },
                      })}
                      dataSource={rows}
                    />
                  ))}
                {yieldRows.length > 0 && (
                  <LendingPanel
                    localStorageKey={`${platform}YieldRowsCollapse`}
                    platform={platformName}
                    title={`${lending.leveragedYieldFarming}`}
                    totalValue={platformTotalNetLeveragedYieldValue}
                    columns={getColumns({
                      map: yieldColumnsMap,
                      args: {
                        commonText: common,
                        lendingText: lending,
                        tableText: table,
                      },
                    })}
                    dataSource={yieldRows}
                  />
                )}
                {(platform === "ratio" || platform === "hedge") &&
                  Object.entries(isolatedVaults).map(([vaultId, { rows, totalNetValue, vaultPlatform, vaultName }]) => {
                    const vaultPlatformString = vaultPlatform ? ` (${vaultPlatform})` : "";
                    return (
                      <LendingPanel
                        key={`${platform}-${vaultId + vaultPlatform}-vault`}
                        localStorageKey={`${platform + vaultId + vaultPlatform}Collapse`}
                        platform={platformName}
                        title={`${vaultName} ${common.vault}${vaultPlatformString}`}
                        totalValue={totalNetValue}
                        columns={getColumns({
                          selector: platformName,
                          map: lendingColumnMap,
                          args: {
                            commonText: common,
                            lendingText: lending,
                            tableText: table,
                          },
                        })}
                        dataSource={rows}
                      />
                    );
                  })}
                {isNFTLendingData(lendingData) && lendingData.nftLending.nftCollateral.length > 0 && (
                  <LendingPanel
                    localStorageKey={`${platform}NFTRowsCollapse`}
                    platform={platformName}
                    totalValue={lendingData.nftLending.totalValueUSD.net}
                    columns={getColumns({
                      map: nftLendingColumnMap,
                      args: {
                        collections: lendingData.nftLending.collections,
                        commonText: common,
                        lendingText: lending,
                        singleAssetStaking,
                        nftGallery,
                        nftModule,
                      },
                    })}
                    dataSource={lendingData.nftLending.nftCollateral}
                  />
                )}
              </div>
            );
          })}
        </>
      </ChevronCollapse>
    </LendingContainer>
  ) : null;
};

const CardContainer = styled.div`
  display: flex;
  gap: 10px;
  @media screen and (max-width: ${Breakpoints.S}px) {
    flex-direction: column;
  }
`;

const LendingContainer = styled.div`
  margin-top: 20px;
`;

const StyledCard = styled<CardInterface>(Card)`
  background: ${darkGrey};
  height: 130px;
  width: 50%;
  border-radius: 8px;
  display: flex;
  align-items: center;

  .ant-card-head {
    border-bottom: 0;

    .ant-card-head-wrapper .ant-card-head-title {
      padding: 4px 0;
      font-weight: normal;
      font-size: 14px;
      color: ${lightGrey2};
      bottom: 0;
    }
  }

  .ant-card-body {
    font-weight: 800;
    font-size: 30px;
    color: #ffffff;
    padding: 16px 24px;
  }

  @media screen and (max-width: ${Breakpoints.S}px) {
    width: 100%;
  }
`;

const StyledStatistic = styled(Statistic)`
  .ant-statistic-title {
    font-family: ${PLUS_JAKARTA_SANS};
    font-weight: 400;
  }

  .ant-statistic-content {
    font-family: Space Mono;
  }
`;
